import { BURGER_DOUBLE_BEEF, BOWL_DOUBLE_BEEF, CategoryWizard, ItemActionType } from '@/apps/menu/constants';
import { CATEGORY_ID, PRODUCTS_ID } from '@/config/constants';

/**
 * @param {string} categoryKey
 * @returns {boolean}
 */
function isDrinkCategory(categoryKey) {
  return [
    CATEGORY_ID.KIDS_BUNDLE_DRINK,
    CATEGORY_ID.BUNDLE_DRINK,
    CATEGORY_ID.GRILLED_CHEESE_BUNDLE_DRINK,
    CATEGORY_ID.TREASURE_BOX_BUNDLE_DRINK,
  ].includes(categoryKey);
}

/**
 * @param {string} categoryKey
 * @returns {boolean}
 */
function isGrilledCategory(categoryKey) {
  return [
    CATEGORY_ID.KIDS_GRILLED_CHEESE,
    CATEGORY_ID.GRILLED_CHICKEN_STRIPS,
    CATEGORY_ID.KIDS_FRIES,
    CATEGORY_ID.KIDS_DRINKS,
    CATEGORY_ID.KIDS_DIPPERS,
    CATEGORY_ID.KIDS_TREASURE_BOX,
    CATEGORY_ID.APPETIZERS,
  ].includes(categoryKey);
}

/**
 * @param {string} categoryKey
 * @returns {boolean}
 */
function isBurgerProduct(productKey) {
  if (productKey == null) {
    return false;
  }

  return [
    PRODUCTS_ID.BOSS_BURGER,
    PRODUCTS_ID.KIDS_BURGER,
    PRODUCTS_ID.DRINKS,
  ].includes(productKey.toString());
}

/**
 * @param {ProductPartId} categoryId
 */
function isCheeseCategory(categoryId) {
  return [
    CATEGORY_ID.BOWL_CHEESE,
    CATEGORY_ID.BURGER_CHEESE,
  ].includes(Number(categoryId));
}

/**
 * @param {ProductPartId} categoryId
 */
function isFriesDippersCategory(categoryId) {
  return [
    CATEGORY_ID.FRIES_DIPPERS,
    CATEGORY_ID.KIDS_FRIES_DIPPERS,
    CATEGORY_ID.GRILLED_CHICKEN_STRIPS_DIPPERS,
    CATEGORY_ID.BUNDLE_SAUCES,
    CATEGORY_ID.APPETIZER_DIPPERS,
  ].includes(Number(categoryId));
}

/**
 * Util function to get selected product parts as a flat array.
 * @param {ProductTemplate} productTemplate
 * @returns {ProductPartItemId[]}
 */
function getProductPartsFromTemplate(productTemplate) {
  return Object.keys(productTemplate);
}

/**
  * @param {FullProductPartTemplate[]} items Product part items.
  * @returns {number} Price.
  */
function calculateDefaultProductPartPrice(items) {
  return items.reduce((
    acc,
    {
      item:
      {
        price: additionalPrice,
        productPrice: initialPrice,
      },
      itemTemplate: { quantity },
    },
  ) => {
    // In case initial price is present, the price for first item is different
    if (initialPrice > 0) {
      const additionalQuantity = quantity > 1 ? quantity - 1 : 0;

      return acc + initialPrice + (additionalPrice * additionalQuantity);
    }

    return acc + (additionalPrice * quantity);
  }, 0);
}

/**
  * @param {FullProductPartTemplate[]} items Product part items.
  * @returns {number} Price.
  */
function calculatePriceWithOneFreeItem(items) {
  return calculateDefaultProductPartPrice(items
    .filter(({ item: { price } }) => price > 0)
    .slice(0, -1));
}

/**
 * Calculates price of cheeses when double beef is chosen.
 * @param {FullProductPartTemplate[]} items Cheese items.
 * @returns {number} Price.
 */
function calculateCheeseWithDoubleProtein(items) {
  if (items.length === 0) {
    return 0;
  }

  const totalCheeseAmount = items.reduce((
    acc,
    { itemTemplate: { quantity } },
  ) => acc + quantity, 0);
  // Assuming that every cheese has the same price.
  const cheesePrice = items[0].item.price;

  const totalCheesePrice = calculateDefaultProductPartPrice(items);

  if (totalCheeseAmount > 1) {
    return totalCheesePrice - cheesePrice;
  }

  return totalCheesePrice;
}

/**
 * @property product - key for selected product in menu.
 * @property orderItem - dish to be build in configurator.
 */
export const state = {
  changedItem:      null,
  product:          null,
  orderItem:        {},
  orderItemId:      null,
  orderItemWatcher: false,
  quantity:         1,
  showHelp:         false,
};

/**
 * @property currentProductKey - returns key for product selected in menu
 * or default value.
 * @property orderItem - returns order item object.
 */
export const getters = {
  currentProductKey: state => state.product || 1,
  orderItem:         state => state.orderItem,
  orderItemId:       state => state.orderItemId,
  orderItemWatcher:  state => state.orderItemWatcher,
  changedItem:       state => state.changedItem,

  /**
   * @returns {(productPartId: ProductPartId, productTemplate: ProductTemplate) => number}
   */
  calculateProductPartPrice: (_, getters) =>
    (productPartId, productTemplate) => {
      const productPartTemplate = productTemplate[productPartId];

      if (productTemplate == null || productPartTemplate == null) {
        return 0;
      }

      /** @type {FullProductPartTemplate[]} */
      const items = Object.entries(productPartTemplate)
        .filter(([key]) => key !== 'radio')
        .reduce((innerAcc, [ itemId, itemTemplate ]) => [
          ...innerAcc,
          {
            item: getters.itemByKey(itemId),
            id:   itemId,
            itemTemplate,
          },
        ], []);

      const isDoubleBeefSelected =
        (productTemplate[CATEGORY_ID.BURGER_PROTEIN] &&
        productTemplate[CATEGORY_ID.BURGER_PROTEIN][BURGER_DOUBLE_BEEF]) ||
        (productTemplate[CATEGORY_ID.BOWL_PROTEIN] &&
        productTemplate[CATEGORY_ID.BOWL_PROTEIN][BOWL_DOUBLE_BEEF]);

      if (isCheeseCategory(productPartId) && isDoubleBeefSelected) {
        return calculateCheeseWithDoubleProtein(items);
      } else if (isFriesDippersCategory(productPartId)) {
        return calculatePriceWithOneFreeItem(items);
      }

      return calculateDefaultProductPartPrice(items);
    },

  /**
   * Calculates price for bundle items.
   * @type {import('../../../store').VuexGetter<PriceQuery>}
   */
  calculateBundlePriceForProductTemplate: (_, getters) => productTemplate =>
    getProductPartsFromTemplate(productTemplate)
      .filter(productPartId => getters.isBundleCategory(productPartId))
      .reduce(
        (totalPrice, productPartId) =>
          totalPrice +
          getters.calculateProductPartPrice(productPartId, productTemplate),
        0
      ),

  /**
   * Price for the items that are not in the bundle.
   * @type {import('../../../store').VuexGetter<PriceQuery>}
   */
  calculateMainPriceForProductTemplate: (_, getters) => productTemplate =>
    getProductPartsFromTemplate(productTemplate)
      .filter(productPartId => !getters.isBundleCategory(productPartId))
      .reduce(
        (totalPrice, productPartId) =>
          totalPrice +
          getters.calculateProductPartPrice(productPartId, productTemplate),
        0
      ),

  /** @type {import('../../../store').VuexGetter<PriceQuery>} */
  calculateTotalPriceForProductTemplate: (_, getters) => productTemplate =>
    getters.calculateBundlePriceForProductTemplate(productTemplate) +
    getters.calculateMainPriceForProductTemplate(productTemplate),
  /** Represents an order item as a flat list of "atomic" items from the product categories. */
  makeOrderItemFlat: (_, getters) => orderItem => Object.values(orderItem)
    .reduce((acc, categoryDescription) =>
      [ ...acc, ...Object.entries(categoryDescription) ], [])
    // TODO (Chernodub V.): this check is repeated several times in the codebase.
    // I have no idea why we have 'radio' in the order dictionary, we need to generalize this "radio"-filtering logic
    .filter(([key]) => key !== 'radio')
    .map(([ key, value ]) => ({
      ...getters.itemByKey(key),
      quantity: value.quantity,
      modifier: value.modifier,
      key,
    })),
  selectedItems: (_state, getters) =>
    getters.makeOrderItemFlat(getters.orderItem),
  // Drinks, grilled products, etc. Standalone items may at the same time be inside a bundle. Therefore they will both be "bundle" and "standalone" items.
  isStandaloneItem: (_, getters) => itemKey =>
    getters.isStandaloneItemCategory(getters.itemByKey(itemKey).categoryKey),
  isStandaloneItemCategory: (_, getters) => categoryKey =>
    getters.currentProductKey === PRODUCTS_ID.DRINKS ||
    isDrinkCategory(categoryKey) ||
    isGrilledCategory(categoryKey),
  // Fries with drinks combined
  isBundleItem: (_, getters) =>
    itemKey => getters.isBundleCategory(getters.itemByKey(itemKey).categoryKey),

  /**
   * Whether category related to fries dippers or not.
   * @param {ProductPartId} categoryKey Category ID.
   * @returns {boolean} Result.
   */
  isFriesDippersCategory: () => categoryKey => isFriesDippersCategory(categoryKey),

  isBundleCategory: (_state, getters) => (categoryKey) => {
    const FRIES_PRODUCTS = [
      PRODUCTS_ID.BOSS_FRIES,
      PRODUCTS_ID.SIMPLE_FRIES,
    ];

    if (FRIES_PRODUCTS.includes(getters.currentProductKey)) {
      return true;
    }

    const currentProduct = getters.productByKey(getters.currentProductKey);
    const currentProductCategories =
      (currentProduct ? currentProduct.categories : [])
        .map(categoryKey => getters.categoryByKey(categoryKey));

    // Looking for a category that represents a link to another product (bundle, in this case).
    // The bundle product itself has several categories that are basically related to a current product via link category.
    // Not sure why it's implemented this way (probably, to avoid data repetitiveness), so I had to hardcode this.
    const categoryLinkToProductBundle = currentProductCategories
      .find(({ wizardType }) => wizardType === CategoryWizard.CHILD_WIZARD);

    if (categoryLinkToProductBundle == null) {
      return false;
    }

    const bundleCategoryKeys = getters
      .productByKey(categoryLinkToProductBundle.subProductId)
      .categories;

    return bundleCategoryKeys.find(key =>
      categoryKey === key || Number(categoryKey) === key);
  },
  isBowlPartCategory: (_, getters) => (categoryKey) => {
    if (getters.isStandaloneItemCategory(categoryKey)) {
      return false;
    }

    const selectedBurgerStyles =
      Object.keys(getters.orderItem[CATEGORY_ID.BURGER_STYLE] || {});

    return selectedBurgerStyles
      .filter(key => key !== 'radio')
      .map(key => getters.itemByKey(key))
      .find(({ name }) => name.toLowerCase().includes('bowl'));
  },
  /** Whether the "boss your burger" product is being configured */
  isBurgerBossing: (_, getters) =>
    getters.currentProductKey.toString() === PRODUCTS_ID.BOSS_BURGER,
  isBurgerPartCategory: (_, getters) => categoryKey =>
    isBurgerProduct(getters.currentProductKey) &&
    !getters.isStandaloneItemCategory(categoryKey) &&
    !getters.isBowlPartCategory(categoryKey),
  configuratorQuantity:   state => state.quantity,
  configuratorHelpShow:   state => state.showHelp,
  itemSelectedInCategory: (state, getters) => (categoryKey) => {
    const orderItemCategory = Object.assign({}, state.orderItem[categoryKey]);

    if (!orderItemCategory) {
      return false;
    }

    if ('radio' in orderItemCategory) {
      delete orderItemCategory.radio;
    }

    const category = getters.categoryByKey(categoryKey);

    // If category doesn't have sub product id just check it's length
    if (
      !Object.prototype.hasOwnProperty.call(category, 'subProductId')
      ||
      !category.subProductId
    ) {
      return Object.keys(orderItemCategory || {}).length !== 0;
    }

    // Otherwise we should check nested categories
    const subProduct = getters.productByKey(category.subProductId);

    // Iter over nested categories
    for (let index = 0; index < subProduct.categories.length; index++) {
      const subCategoryId = subProduct.categories[index];

      if (!(subCategoryId.toString() in state.orderItem)) {
        return false;
      }

      // Check that selected item is of type BREAK_WIZARD. If so we should
      // omit further categories.
      const subItemId = Object.keys(state.orderItem[subCategoryId])[0];

      if (!subItemId) {
        return false;
      }

      const subItem = getters.itemByKey(subItemId);

      if (subItem && subItem.actionType === ItemActionType.BREAK_WIZARD) {
        return true;
      }
    }

    return true;
  },
  orderItemCompleted: (state, getters) => {
    if (Object.keys(state.orderItem).length === 0 && !state.orderItemId) {
      return true;
    }

    const product = getters.productByKey(state.product);

    if (product.categories.length === 0) {
      return false;
    }

    for (let index = 0; index < product.categories.length; index++) {
      const categoryId = product.categories[index];
      const bundleCategoryId = 71;
      const friesId = 72;
      const dippersId = 73;

      // We should define order as completed when all categories has its content but drinks
      // All bundle products has the same category in product - 71, that's why we should additionally check orderItem categories
      const onlyDrinkNotSelected = categoryId === bundleCategoryId
       && !!state.orderItem[friesId]
       && !!state.orderItem[dippersId];

      if (!getters.itemSelectedInCategory(categoryId)
      && !onlyDrinkNotSelected) {
        return false;
      }
    }

    return true;
  },
  bundleProducts: (state) => {
    const { orderItem } = state;
    const productsList = [];

    Object.keys(orderItem).forEach((categoryKey) => {
      const productCategory = orderItem[categoryKey];

      Object.keys(productCategory).forEach((productKey) => {
        const product = productCategory[productKey];

        if (product.bundleId) {
          productsList.push({
            ...product,
            productKey,
          });
        }
      });
    });

    return productsList;
  },
};

export default {
  state,
  getters,
};



// WEBPACK FOOTER //
// ./src/apps/configurator/store/state.js